<template>
  <div class="container-fluid">
    <Breadcrumbs
      :title="'Prompt Templates'"
      :buttonLabel="'Create Prompt'"
      @buttonClick="createPrompt"
      :disabled="!canCreate('main')"
    />

    <!-- Prompt Table -->
    <CommonTable2
      :tableObj="tableConfig"
      :title="'Prompt Templates'"
      permissionKey="main"
      @edit="handleEdit"
      @delete="handleDelete"
      @view="handleView"
    />

    <!-- Pagination -->
    <div
      v-if="!crud.isLoading && crud.data.value.length > 0"
      class="d-flex justify-content-end align-items-center gap-3 mt-3 mb-4 flex-wrap"
    >
      <div class="d-flex align-items-center gap-2">
        <span class="text-muted">Rows per page:</span>
        <select
          class="form-select form-select-sm rows-select"
          v-model.number="tablePagination.per_page"
          @change="changeRows"
        >
          <option v-for="n in [5, 10, 20, 50, 100]" :key="n" :value="n">
            {{ n }}
          </option>
        </select>
      </div>

      <div class="pagination-mini">
        <button
          class="page-btn prev_btn"
          :disabled="!tablePagination.has_prev"
          @click="handlePageChange(tablePagination.current_page - 1)"
        >
          «
        </button>

        <button
          v-for="(p, index) in visiblePages"
          :key="index"
          class="page-btn"
          :class="{
            'page-active': p === tablePagination.current_page,
            'disabled-dots': p === '...',
          }"
          :disabled="p === '...'"
          @click="typeof p === 'number' && handlePageChange(p)"
        >
          {{ p }}
        </button>

        <button
          class="page-btn next_btn"
          :disabled="!tablePagination.has_next"
          @click="handlePageChange(tablePagination.current_page + 1)"
        >
          »
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useCrud } from "@/composables/commen/useCrud"; // Adjust path
import BaseModal from "@/components/commen/modal/BaseModal.vue"; // Adjust path
import BaseForm from "@/components/commen/modal/BaseForm.vue"; // Adjust path
// import zeroConfiguration from "@/components/commen/table/zeroConfiguration.vue";
import { useRouter } from "vue-router";
import CommonTable2 from "@/components/commen/table/CommonTable2.vue";
import { usePermissions } from "@/composables/usePermissions";

const { canCreate } = usePermissions();

const router = useRouter();
const API_RESOURCE = "/common/prompts";
const crud = useCrud(API_RESOURCE);

const isModalOpen = ref(false);
const isViewMode = ref(false);
const isEditMode = ref(false);
const formData = ref({
  title: "",
  target: "",
  prompt: "",
  variables: [],
  isReusable: false,
});

// --- Pagination State ---
const tablePagination = ref({
  current_page: 1,
  per_page: 10,
  total: 0,
  total_pages: 0,
  has_next: false,
  has_prev: false,
});

const visiblePages = computed(() => {
  const total = tablePagination.value.total_pages;
  const current = tablePagination.value.current_page;

  if (!total || total <= 4) {
    return Array.from({ length: Math.max(1, total) }, (_, i) => i + 1);
  }

  const pages: (number | string)[] = [];
  pages.push(1);

  if (current > 4) pages.push("...");

  const windowStart = Math.max(2, current - 1);
  const windowEnd = Math.min(total - 1, current + 1);
  for (let i = windowStart; i <= windowEnd; i++) pages.push(i);

  if (current < total - 3) pages.push("...");

  pages.push(total);
  return pages;
});

const tableConfig = ref({
  columns: [
    { name: "Title", field: "title" },
    { name: "Category", field: "prompt_category_title" },
    { name: "Base Prompt", field: "base_prompt_title" },
  ],
  data: crud.data.value,
  actions: [true],
  loading: crud.isLoading.value,
  showCategoryFilter: false,
  showStatusFilter: false,
  table_width: "max-content",
});

watch(crud.data, (newData) => {
  tableConfig.value.data = newData;
});

watch(crud.isLoading, (val) => {
  tableConfig.value.loading = val;
});

watch(crud.pagination, (newPagination) => {
  if (newPagination && Object.keys(newPagination).length > 0) {
    tablePagination.value.current_page = newPagination.current_page || newPagination.currentPage || newPagination.page || 1;
    tablePagination.value.total_pages = newPagination.total_pages || newPagination.totalPages || Math.ceil((newPagination.total || newPagination.totalItems || 0) / tablePagination.value.per_page) || 1;
    tablePagination.value.total = newPagination.total || newPagination.totalItems || 0;
    tablePagination.value.has_prev = tablePagination.value.current_page > 1;
    tablePagination.value.has_next = tablePagination.value.current_page < tablePagination.value.total_pages;
  }
}, { deep: true });

const changeRows = async () => {
  tablePagination.value.current_page = 1;
  await crud.fetchData({ page: 1, limit: tablePagination.value.per_page });
};

const handlePageChange = async (page: number) => {
  if (typeof page !== "number" || Number.isNaN(page)) return;
  if (page < 1 || page === tablePagination.value.current_page) return;
  if (tablePagination.value.total_pages && page > tablePagination.value.total_pages) {
    return;
  }
  await crud.fetchData({ page, limit: tablePagination.value.per_page });
};

const createPrompt = () => router.push("/prompt-create");

const formFields = computed(() => [
  { key: "title", label: "Title", type: "text", required: true },
  {
    key: "target",
    label: "Target",
    type: "select",
    required: true,
    options: [
      { title: "Workspace", value: "workspace" },
      { title: "Lane", value: "lane" },
      { title: "Module", value: "module" },
      { title: "Sheet", value: "sheet" },
      { title: "CardList", value: "cardlist" },
      { title: "Card", value: "card" },
      { title: "Shared", value: "shared" },
    ],
  },
  {
    key: "prompt",
    label: "Prompt",
    type: "textarea",
    required: true,
    placeholder: "Use {placeholders}",
  },
  {
    key: "variables",
    label: "Variables",
    type: "chip",
    placeholder: "Enter variables (e.g. workspace-title)",
  },
  { key: "isReusable", label: "Reusable Snippet", type: "checkbox" },
]);

const isFormInvalid = computed(
  () => !formData.value.title?.trim() || !formData.value.prompt?.trim()
);

function openModal(mode: "create" | "edit" | "view", data: any = null) {
  isModalOpen.value = true;
  isViewMode.value = mode === "view";
  isEditMode.value = mode === "edit";
  formData.value = data
    ? { ...data, variables: data.variables || [] }
    : { title: "", target: "", prompt: "", variables: [], isReusable: false };
}

function closeModal() {
  isModalOpen.value = false;
  isViewMode.value = false;
  isEditMode.value = false;
  formData.value = {
    title: "",
    target: "",
    prompt: "",
    variables: [],
    isReusable: false,
  };
}

const handleEdit = (row: any) =>
  router.push({ path: "/prompt-edit", query: { id: row._id } });
const handleView = (row: any) =>
  router.push({ path: "/prompt-edit", query: { id: row._id, view: "1" } });

async function handleDelete(id: string) {
  if (confirm("You can't delete this prompt it is in use")) {
    //await crud.deleteItem(id);
    //await crud.fetchData({ page: tablePagination.value.current_page, limit: tablePagination.value.per_page });
  }
}

async function handleSave() {
  if (isFormInvalid.value) return;
  const { _id, ...data } = formData.value;
  const result = _id
    ? await crud.updateItem(_id, data)
    : await crud.createItem(data);
  if (result) {
    closeModal();
    if (_id) {
      await crud.fetchData({ page: tablePagination.value.current_page, limit: tablePagination.value.per_page });
    } else {
      await crud.fetchData({ page: 1, limit: tablePagination.value.per_page });
    }
  }
}

crud.fetchData({ page: tablePagination.value.current_page, limit: tablePagination.value.per_page });
</script>

<style scoped>
.rows-select {
  width: 82px;
  padding: 8px 17px;
  font-size: 0.85rem;
  background-color: transparent;
}

.pagination-mini {
  display: flex;
  align-items: center;
  border-radius: 6px;
  overflow: hidden;
}

.page-btn {
  border: none;
  background: transparent;
  font-size: 1rem;
  padding: 7px 12px;
  cursor: pointer;
  color: #6c757d;
  border-radius: 0;
  transition: 0.2s;
  border: 1px solid #dee2e6;
  outline: none;
}

.page-btn:first-child {
  border-top-left-radius: 6px;
  border-bottom-left-radius: 6px;
}

.page-btn:last-child {
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
}

.page-btn:hover {
  background: #e9ecef;
}

.page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.page-active {
  background: #21c1d6;
  color: #fff;
  padding: 7px 12px;
  font-weight: 600;
  min-width: 28px;
  text-align: center;
  display: inline-block;
  border: 1px solid #21c1d6;
}

.page-active:hover {
  background: #21c1d6;
}

.prev_btn,
.next_btn {
  color: #21c1d6;
}
</style>
